// use-form-history.ts
// This file provides the useFormHistory hook for tracking changes to form fields in a local state array.
// Useful for managing unsaved changes and change history in forms.
import { useState } from 'react';

interface FormChange {
  field: string;
  previousValue: string | null;
  newValue: string;
  userMessage: string;
}


/**
 * @description This hook is used to keep track of the changes made to the form fields.
 * @returns  { formChangeHistory: FormChange[], updateChangeHistory: (formChange: FormChange, removeEntry?: boolean) => void, resetFormChangeHistory: () => void }
 * @example
 * // Bring in change history and update function
 * const { formChangeHistory, updateChangeHistory, resetFormChangeHistory } = useFormHistory();
 * // providing a new change to the history list will either add a new change or update an existing one
 * updateChangeHistory({
 *  field: 'email',
 *  previousValue: 'example'
 *  newValue: 'changed value',
 *  userMessage: 'Email changed'
 *  },
 *  true // false is the default value, if set to true it will remove the entry from the history list
 * );
 *
 * // Reset the form change history
 * resetFormChangeHistory();
 */
const useFormHistory = (): { formChangeHistory: FormChange[], updateChangeHistory: (formChange: FormChange, removeEntry?: boolean) => void, resetFormChangeHistory: () => void } => {
  const [formChangeHistory, setFormChangeHistory] = useState<FormChange[]>([]);

  const updateChangeHistory = (formChange: FormChange, removeEntry = false) => {
    const filteredChangeHistory = formChangeHistory.filter(
      (change) => change.field !== formChange.field,
    );
    if (!removeEntry) setFormChangeHistory([...filteredChangeHistory, formChange]); else setFormChangeHistory(filteredChangeHistory);
  };

  const resetFormChangeHistory = () => {
    setFormChangeHistory([]);
  };

  return { formChangeHistory, updateChangeHistory, resetFormChangeHistory };
};

export default useFormHistory;
